import { Anchor, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

/**
 * 输入框注意喚起 + 免责提示(安全対策タスク #4/#5)。
 * 交互参考 ChatGPT:单行胶囊贴在输入框上方,聚焦/滚动到底部时淡入。
 * - 文案:個人情報・機密情報は入力しないでください。回答内容は正確性を保証するものではありません。
 * - floating:绝对定位锚在输入框上方,需挂载在输入框的 relative 容器(form)内部
 * - inline:作为输入区的一部分占位
 *
 * 重要:两种 layout 都「常驻布局、只切 opacity」。若隐藏时直接卸载,inline 下会改变
 * 上方 ChatScroll 的 clientHeight,与 chatAtBottom 判定形成自激振荡(滚到底部临界点
 * 时提示会不停闪烁)。因此 visible 只控制透明度,不控制挂载。
 * 需要彻底不展示(如配置关闭)时,由调用方在外层条件渲染。
 *
 * chat / ChatWidget / Admin playground 三处共用。
 */
export const InputDisclaimer = ({
  visible = true,
  onTermsClick,
  className,
  variant = 'default',
  layout = 'floating',
}: {
  /** 可见态(由挂载方传入 inputFocus / chatAtBottom),不可见时淡出但保留占位 */
  visible?: boolean;
  /** 传入时显示「利用上の注意」链接(利用规约模块提供全文展示) */
  onTermsClick?: () => void;
  className?: string;
  /** overlay:数字人等任意背景之上,用深色胶囊 + 浅色文字保证可读性 */
  variant?: 'default' | 'overlay';
  /** inline:作为输入区的一部分占位,避免在小部件中遮挡末尾对话 */
  layout?: 'floating' | 'inline';
}) => {
  const { t } = useTranslation();
  const isOverlay = variant === 'overlay';
  const isInline = layout === 'inline';

  return (
    <Text
      component="div"
      ta="center"
      aria-hidden={!visible}
      className={`${
        isInline ? 'w-full px-1 mb-1' : 'absolute inset-x-0 bottom-full mb-2'
      } transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'} ${
        className ?? ''
      }`}
    >
      <span
        className={`inline-block max-w-full rounded-full ${
          isInline ? 'px-3 py-1.5 text-[11px]' : 'px-3 py-1 text-[10px]'
        } leading-4 backdrop-blur-sm ${isOverlay ? 'bg-black/55 text-white/85' : 'bg-gray-100/95 text-gray-500'}`}
      >
        <span className={`font-semibold ${isOverlay ? 'text-white/95' : 'text-gray-600'}`}>
          {t('completions.input.privacy_notice')}
        </span>{' '}
        {t('completions.input.accuracy_notice')}
        {onTermsClick && (
          <>
            {' '}
            <Anchor
              component="button"
              type="button"
              onClick={onTermsClick}
              className="text-[10px] font-semibold align-baseline"
            >
              {t('completions.input.terms_link')}
            </Anchor>
          </>
        )}
      </span>
    </Text>
  );
};
